import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import Button from "../../components/Button/Button";
import "./ProductPage.scss";
import interviewCoaching from "../../assets/products/interview-coaching.jpg";
import {
  FaUsers,
  FaUserTie,
  FaComments,
  FaLightbulb,
  FaClipboardCheck,
  FaClock,
  FaVideo,
  FaCalendarAlt
} from "react-icons/fa";

interface InterviewCoachingPageProps {
  product: {
    id: string;
    price: number;
    length: string;
    comingSoon: boolean;
    checkoutUrl?: string;
  };
}

const InterviewCoachingPage = ({ product }: InterviewCoachingPageProps) => {
  const coachingFeatures = [
    {
      icon: <FaUserTie />,
      title: "Leading UK ophthalmologists",
      description:
        "1-to-1 coaching from leading UK ophthalmologists who know the ST1 process inside out—structured, individualised feedback you can act on"
    },
    {
      icon: <FaUsers />,
      title: "Panel insights",
      description:
        "Insights from consultants who serve on real interview panels—what panels look for and how to deliver under pressure"
    },
    {
      icon: <FaComments />,
      title: "Scenario Practice",
      description: "Practice delivery of high-yield scenarios (clinical, communication, professionalism)"
    },
    {
      icon: <FaLightbulb />,
      title: "Answer Techniques",
      description: "Techniques for answering challenging questions with clarity and confidence"
    },
    {
      icon: <FaClipboardCheck />,
      title: "Mock Interviews",
      description: "Mock interview runs with targeted coaching on structure and scoring strategy"
    },
    {
      icon: <FaClock />,
      title: "Full Day Preparation",
      description: "2 sessions (10 hours total) across a full day for comprehensive preparation"
    }
  ];

  const formatFeatures = [
    {
      icon: <FaClock />,
      title: "Full Day Sessions",
      description: "2 comprehensive coaching sessions across a full day"
    },
    {
      icon: <FaVideo />,
      title: "Online Delivery",
      description: "Online delivery via video conferencing (most likely format)"
    },
    {
      icon: <FaComments />,
      title: "Real-Time Feedback",
      description: "Real-time feedback and interactive practice"
    },
    {
      icon: <FaCalendarAlt />,
      title: "Flexible Scheduling",
      description: "Flexible scheduling to fit your preparation timeline"
    }
  ];

  return (
    <>
      <NavBar />
      <div id="ProductPage" className="product-page--oq">
        <section className="oq-product-hero" aria-label="Interview coaching">
          <div className="site-container oq-product-hero__inner">
            <div className="oq-product-hero__copy">
              <div className="interview-coaching-limited-badge">
                <FaUsers aria-hidden />
                <span>Limited spaces — book early to secure your place</span>
              </div>
              <p className="oq-product-hero__eyebrow">ST1 Ophthalmology interviews</p>
              <h1 className="oq-product-hero__title">Interview Coaching</h1>
              <p className="oq-product-hero__lead">
                Structured, one-to-one ST1 interview coaching delivered by{" "}
                <strong>leading UK ophthalmologists</strong>—personalised guidance, mock interviews, and feedback so you
                perform at your best when it counts.
              </p>
              <div className="oq-product-hero__pricing">
                <span className="oq-product-hero__price">£{product.price}</span>
                <span className="oq-product-hero__length">/ {product.length}</span>
                {product.comingSoon && <span className="coming-soon-pill">coming soon</span>}
              </div>
              {!product.comingSoon && (
                <div className="oq-product-hero__signup">
                  <Button
                    text="Buy Interview Coaching Now"
                    linkTo={product.checkoutUrl ?? `/sign-up?product=${product.id}`}
                    type="navy"
                    external={!!product.checkoutUrl}
                  />
                </div>
              )}
            </div>
            <div className="oq-product-hero__visual">
              <div className="oq-product-hero__frame">
                <img
                  className="oq-product-hero__image"
                  src={interviewCoaching}
                  alt="Interview coaching — one-to-one ST1 preparation"
                  width={640}
                  height={363}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        <div className="product-sales-content">
          <div className="site-container">
            <section className="sales-section intro-section">
              <p className="lead-text">
                The Ophthalmology ST interview is highly competitive and requires more than just clinical knowledge.
                Success depends on your ability to communicate clearly, demonstrate professionalism, and present
                yourself confidently under pressure.
              </p>
              <p className="solution-text">
                <strong>Learn from leading UK ophthalmologists who coach candidates through this process every year.</strong>
              </p>
            </section>

            <section className="sales-section interview-coaching-whats-included">
              <h2 className="interview-coaching-section-heading">What&apos;s Included</h2>
              <p className="interview-coaching-section-lead">
                Achieve peak performance with structured, one-to-one coaching sessions tailored to you — focusing on
                refining responses, sharpening clinical reasoning, and improving delivery under real-world interview
                conditions.
              </p>

              <div className="interview-coaching-features-grid">
                {coachingFeatures.map((feature, index) => (
                  <div key={index} className="interview-coaching-feature-card">
                    <div className="interview-coaching-feature-icon">{feature.icon}</div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="sales-section interview-coaching-format-section">
              <div className="interview-coaching-format-panel">
                <h2 className="interview-coaching-format-title">Format &amp; Delivery</h2>
                <p className="interview-coaching-format-lead">
                  Our interview coaching is designed to be accessible and effective. Sessions are conducted online,
                  making it convenient for you to participate from anywhere.
                </p>

                <div className="format-delivery-grid">
                  {formatFeatures.map((feature, index) => (
                    <div key={index} className="interview-coaching-format-item">
                      <div className="interview-coaching-format-item-icon">{feature.icon}</div>
                      <h3>{feature.title}</h3>
                      <p>{feature.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="sales-section pricing-section">
              <div className="pricing-card">
                <h3>Get Started Today</h3>
                <p className="interview-coaching-pricing-limited-pill" role="status">
                  <FaUsers aria-hidden />
                  <span>Limited spots — book early</span>
                </p>
                <div className="pricing-amount">
                  <div className="interview-coaching-price-row">
                    <span className="price-large">£{product.price}</span>
                    <span className="price-period">/ {product.length}</span>
                  </div>
                  {product.comingSoon && (
                    <div className="interview-coaching-coming-soon-wrap">
                      <span className="coming-soon-pill">coming soon</span>
                    </div>
                  )}
                </div>
                <p className="interview-coaching-pricing-note">2 sessions (10 hours total) across a full day</p>
                <p className="interview-coaching-pricing-note interview-coaching-pricing-note--small">
                  Contact us to discuss your interview coaching needs and get started.
                </p>
                <div className="pricing-cta">
                  {!product.comingSoon && (
                    <Button
                      text="Buy now"
                      linkTo={product.checkoutUrl ?? `/sign-up?product=${product.id}`}
                      type="navy"
                      external={!!product.checkoutUrl}
                    />
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default InterviewCoachingPage;
